import { useCallback } from 'react';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useAccountRoleTracking } from '@frameio/shared/plans/hooks';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { reportError, usePerformanceMeasure } from '@frameio/telemetry';
import { ListItem, useSheetContext } from '@frameio/vapor';
import { useUpdateNotificationsReadStatusMutation } from '../../../gql';
import { getScreenReaderTextForTimestamp } from '../../../utils';
import { getLinkForNotificationCell } from '../../../utils/getLinkForNotificationCell';
import { getNotificationTypeForTracking } from '../../../utils/getNotificationTypeForTracking';
import { updateCachedNotificationGroupReadStatus } from '../../../utils/updateCachedNotificationGroupReadStatus';
import { AccessRequestCell } from '../../AccessRequestCell';
import { NotificationCell } from '../../NotificationCell';
import type {
  AccessRequestNode,
  NotificationGroups,
  NotificationLinkClick,
  NotificationsActionCallback,
  UpdateNotificationsReadStatusMutation,
} from '../../NotificationCell/types';
import type { NotificationsListFilters } from '../../NotificationsListFilter/types';
import { ShowAllAccessRequestsButton } from '../../ShowAllAccessRequestsButton';
import TEXT from './generated/TEXT/NotificationListSection';

TEXT.DEFINE({
  /** Accessibility label for access request item in the notifications list */
  accessRequestAccessibilityLabel: (requesterNameOrEmail: string) =>
    `Access request from ${requesterNameOrEmail}`,
});

/**
 * Type guard to check if an item is a NotificationGroup (not an AccessRequest).
 *
 * @param item - A union type that could be either an AccessRequestNode or NotificationGroups
 * @returns True if the item is a NotificationGroups type, false if it's an AccessRequestNode
 *
 * @example
 * ```ts
 * const items: (AccessRequestNode | NotificationGroups)[] = [...];
 * const notificationGroups = items.filter(isNotificationGroupOnly);
 * // TypeScript now knows notificationGroups is NotificationGroups[]
 * ```
 */
const isNotificationGroupOnly = (
  item: AccessRequestNode | NotificationGroups
): item is NotificationGroups => item.__typename !== 'AccessRequest';

export function useNotificationListSection({
  filter,
}: {
  filter: NotificationsListFilters;
}) {
  const accountId = useActiveAccountId();
  const { onRequestClose } = useSheetContext();
  const track = useTrackUnsafe();
  const accountRoleNameForTracking = useAccountRoleTracking({
    includeRoleName: true,
  });
  const {
    beginMeasurement: beginPageNavigationFromNotificationCellMeasurement,
  } = usePerformanceMeasure('page_navigation_from_notification');

  const [
    updateNotificationsReadStatus,
    { error: updateNotificationsReadStatusError },
  ] = useUpdateNotificationsReadStatusMutation();

  const updateNotificationsReadStatusMutation = useCallback(
    ({
      notificationId,
      notificationIds,
      isNotificationGroupRead,
      typename,
      skipAnalytics = false,
      context,
    }: UpdateNotificationsReadStatusMutation) => {
      updateNotificationsReadStatus({
        variables: {
          input: {
            accountId,
            notificationIds,
            readStatus: isNotificationGroupRead ? 'UNREAD' : 'READ',
          },
        },
        onCompleted: () => {
          if (skipAnalytics) return;
          track('notification-read-status-updated', {
            notification_type: getNotificationTypeForTracking({
              substringToRemove: 'NotificationGroup',
              typename,
            }),
            updated_read_status: isNotificationGroupRead ? 'unread' : 'read',
            ...(context ? { context } : {}),
          });
        },
        onError: (err) => {
          reportError(err, {
            message: 'Failed to update notification read status',
            updateNotificationsReadStatusError,
          });
        },
        update: () => {
          updateCachedNotificationGroupReadStatus({
            accountId,
            notificationId,
            filter,
          });
        },
        optimisticResponse: {
          __typename: 'Mutation',
          updateNotificationsReadStatus: {
            __typename: 'UpdateNotificationsReadStatusPayload',
            successful: true,
          },
        },
      });
    },
    [
      accountId,
      filter,
      track,
      updateNotificationsReadStatus,
      updateNotificationsReadStatusError,
    ]
  );

  const onNotificationLinkClick = useCallback(
    ({
      isNotificationGroupRead,
      notificationId,
      notificationIds,
      notifications,
      shouldCloseSheet,
      typename,
      context,
    }: NotificationLinkClick) => {
      beginPageNavigationFromNotificationCellMeasurement();
      if (shouldCloseSheet !== false) {
        onRequestClose();
      }
      /** Always mark the notification group as read when clicking on the
       * notification cell */
      if (!isNotificationGroupRead) {
        updateNotificationsReadStatusMutation({
          notificationId,
          notificationIds,
          isNotificationGroupRead,
          typename,
          skipAnalytics: true,
        });
      }

      track('notification-actioned', {
        notification_count: notifications.length,
        notification_type: getNotificationTypeForTracking({
          substringToRemove: 'NotificationGroup',
          typename,
        }),
        read_status: isNotificationGroupRead ? 'read' : 'unread',
        role_name: accountRoleNameForTracking?.name,
        ...(context ? { context } : {}),
      });
    },
    [
      accountRoleNameForTracking,
      beginPageNavigationFromNotificationCellMeasurement,
      onRequestClose,
      track,
      updateNotificationsReadStatusMutation,
    ]
  );
  return (item: AccessRequestNode | NotificationGroups) => {
    /** if the item is the show all access requests button,
     * we don't want to the list item wrapper to be interactive,
     * so we set the isDisabled prop to true
     */
    if (item.id === 'show-all-access-requests') {
      return (
        <ListItem isDisabled={true}>
          <ShowAllAccessRequestsButton />
        </ListItem>
      );
    }
    if (item.__typename === 'AccessRequest') {
      const { __typename, id, requestedAt, requestedResource, requesterUser } =
        item;

      const href = getLinkForNotificationCell({
        link: {
          __typename,
          id,
          requestedResource,
        },
      });

      const accessibilityTimestampLabel = requestedAt
        ? getScreenReaderTextForTimestamp({
            timestamp: requestedAt,
          })
        : '';

      let textValue = '';

      if (requesterUser?.name || requesterUser?.email) {
        const requesterUserNameOrEmail =
          requesterUser?.name || requesterUser?.email;
        const accessRequestAccessibilityLabel =
          TEXT.accessRequestAccessibilityLabel(requesterUserNameOrEmail);

        textValue = `${accessibilityTimestampLabel} ${accessRequestAccessibilityLabel}`;
      } else {
        textValue = `${accessibilityTimestampLabel}`;
      }

      return (
        <ListItem
          data-testid="access-request-list-item"
          href={href}
          onAction={onRequestClose}
          textValue={textValue}
        >
          <AccessRequestCell id={id || ''} />
        </ListItem>
      );
    }
    if (!isNotificationGroupOnly(item)) return null;

    const { id, isRead, mostRecentEventOccurredAt } = item;
    if (!('content' in item)) return <></>;

    const href = getLinkForNotificationCell({
      link: item.content.link,
    });

    const isShareNotification =
      item.content.link.__typename === 'NotificationLinkCommentViaShare';

    const shareId =
      'share' in item.content.link ? item.content.link.share?.id : undefined;

    const projectId =
      'project' in item.content.link
        ? item.content.link.project?.id
        : undefined;

    const info = {
      notificationId: id,
      notificationIds: item.notifications.map((n) => n.id),
      isNotificationGroupRead: isRead,
      notifications: item.notifications,
      typename: item.__typename,
    };

    const accessibilityContentLabel = item.content.layout.header
      .map((header) => {
        return 'content' in header ? header.content : '';
      })
      .join(' ');

    const accessibilityTimestampLabel = getScreenReaderTextForTimestamp({
      timestamp: mostRecentEventOccurredAt,
    });

    return (
      <ListItem
        data-testid="notification-list-item"
        href={href}
        onAction={() => {
          onNotificationLinkClick(info);
        }}
        {...(isShareNotification && {
          target: '_blank',
          rel: 'noopener noreferrer',
        })}
        textValue={`${accessibilityTimestampLabel} ${accessibilityContentLabel}`}
      >
        <NotificationCell
          href={href}
          id={item.id}
          isRead={isRead}
          notificationType={item.__typename}
          onNotificationLinkClick={({ context }: NotificationsActionCallback) =>
            onNotificationLinkClick({
              ...info,
              shouldCloseSheet: false,
              context,
            })
          }
          projectId={projectId}
          shareId={shareId}
          updateNotificationsReadStatus={({
            context,
          }: NotificationsActionCallback) => {
            updateNotificationsReadStatusMutation({
              ...info,
              context,
            });
          }}
        />
      </ListItem>
    );
  };
}
